/**
 * StrategyInfoPanel
 *
 * Renders the strategy-specific info card that sits in the left panel of the
 * backtest UIs, below P&L. Each strategy type gets its own layout:
 *   - PYRAMID_HEDGING / FUTURES_PYRAMID_HEDGING → LadderView (handled by caller)
 *   - DELTA_HEDGED_SHORT_STRANGLE              → DhssPanel
 *   - WEEKLY_INDEX_SHORT_STRADDLE              → WissPanel
 */

import React from "react";

// ── helpers ───────────────────────────────────────────────────────────────────

function fmt2(n)    { return n == null ? "—" : Number(n).toFixed(2); }
function fmtPct(n)  { return n == null ? "—" : `${Number(n).toFixed(1)}%`; }
function fmtDollar(n) {
  if (n == null) return "—";
  const abs = `$${Math.abs(Number(n)).toFixed(2)}`;
  return n < 0 ? `−${abs}` : abs;
}

function InfoRow({ label, value, color }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "4px 0", borderBottom: "1px solid #f5f5f5" }}>
      <span style={{ fontSize: 11, color: "#888" }}>{label}</span>
      <span style={{ fontSize: 12, fontWeight: 600, color: color || "#2c3e50" }}>{value}</span>
    </div>
  );
}

function StatChip({ label, value, color }) {
  return (
    <div style={{ background: "#f8f9fa", borderRadius: 6, padding: "6px 10px", minWidth: 70 }}>
      <div style={{ fontSize: 10, color: "#999", marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: color || "#2c3e50" }}>{value}</div>
    </div>
  );
}

// ── DHSS panel ────────────────────────────────────────────────────────────────

function DhssPanel({ frame }) {
  const options = frame?.positions?.options || [];
  const ltp     = frame?.stock_ltp || 0;

  const calls = options.filter(o => ["CE", "C", "call"].includes((o.type || "").toUpperCase()));
  const puts  = options.filter(o => ["PE", "P", "put"].includes((o.type || "").toUpperCase()));

  const netDelta = options.reduce((s, o) => s + (Number(o.delta) || 0) * (Number(o.netqty) || 0), 0);
  const netTheta = options.reduce((s, o) => s + (Number(o.theta) || 0) * Math.abs(Number(o.netqty) || 0), 0);
  const totalUnrealised = options.reduce((s, o) => s + (Number(o.unrealised) || 0), 0);

  const callStrike = calls[0]?.strike;
  const putStrike  = puts[0]?.strike;
  const strangleWidth = callStrike && putStrike ? Math.abs(callStrike - putStrike) : null;
  const callDist = callStrike && ltp ? ((callStrike - ltp) / ltp * 100) : null;
  const putDist  = putStrike  && ltp ? ((ltp - putStrike)  / ltp * 100) : null;

  const deltaColor = Math.abs(netDelta) < 0.1 ? "#28a745" : Math.abs(netDelta) < 0.3 ? "#fd7e14" : "#dc3545";

  if (options.length === 0) {
    return (
      <div style={emptyStyle}>
        No option positions yet.<br />Suggestions will appear when rules fire.
      </div>
    );
  }

  return (
    <div>
      {/* Key metrics row */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
        <StatChip label="Net Δ"     value={fmt2(netDelta)} color={deltaColor} />
        <StatChip label="Theta/day" value={fmtDollar(netTheta)} color="#28a745" />
        <StatChip label="Width"     value={strangleWidth ? `$${strangleWidth}` : "—"} />
      </div>

      {/* Call leg */}
      {calls.length > 0 && (
        <>
          <div style={legHeader}>Short Call</div>
          {calls.map((o, i) => (
            <div key={i}>
              <InfoRow label="Strike"   value={`$${o.strike}`} />
              <InfoRow label="Qty"      value={`${o.netqty} × ${o.lots ?? 1} lots`} />
              <InfoRow label="LTP"      value={fmtDollar(o.ltp)} />
              <InfoRow label="Delta"    value={fmt2(o.delta)} />
              <InfoRow label="Distance" value={callDist != null ? fmtPct(callDist) + " OTM" : "—"} color={callDist != null && callDist < 3 ? "#dc3545" : "#28a745"} />
            </div>
          ))}
        </>
      )}

      {/* Put leg */}
      {puts.length > 0 && (
        <>
          <div style={{ ...legHeader, marginTop: 8 }}>Short Put</div>
          {puts.map((o, i) => (
            <div key={i}>
              <InfoRow label="Strike"   value={`$${o.strike}`} />
              <InfoRow label="Qty"      value={`${o.netqty} × ${o.lots ?? 1} lots`} />
              <InfoRow label="LTP"      value={fmtDollar(o.ltp)} />
              <InfoRow label="Delta"    value={fmt2(o.delta)} />
              <InfoRow label="Distance" value={putDist != null ? fmtPct(putDist) + " OTM" : "—"} color={putDist != null && putDist < 3 ? "#dc3545" : "#28a745"} />
            </div>
          ))}
        </>
      )}

      {/* Net unrealised */}
      <div style={{ marginTop: 8, padding: "6px 0", borderTop: "1px solid #e9ecef" }}>
        <InfoRow
          label="Net Options P&L"
          value={fmtDollar(totalUnrealised)}
          color={totalUnrealised >= 0 ? "#28a745" : "#dc3545"}
        />
      </div>
    </div>
  );
}

// ── WISS panel ────────────────────────────────────────────────────────────────

function WissPanel({ frame }) {
  const options = frame?.positions?.options || [];
  const ltp     = frame?.stock_ltp || 0;

  const calls = options.filter(o => ["CE", "C", "call"].includes((o.type || "").toUpperCase()));
  const puts  = options.filter(o => ["PE", "P", "put"].includes((o.type || "").toUpperCase()));

  // For a straddle, calls and puts share the same strike
  const straddleStrike = calls[0]?.strike || puts[0]?.strike;
  const distFromStrike = straddleStrike && ltp
    ? ((ltp - straddleStrike) / straddleStrike * 100)
    : null;
  const absDistPct = distFromStrike != null ? Math.abs(distFromStrike) : null;

  const netTheta = options.reduce((s, o) => s + (Number(o.theta) || 0) * Math.abs(Number(o.netqty) || 0), 0);
  const netDelta = options.reduce((s, o) => s + (Number(o.delta) || 0) * (Number(o.netqty) || 0), 0);
  const totalUnrealised = options.reduce((s, o) => s + (Number(o.unrealised) || 0), 0);
  const totalPremium = options.reduce((s, o) => s + (Number(o.avg_price) || 0) * Math.abs(Number(o.netqty) || 0), 0);

  const riskColor = absDistPct == null ? "#888"
    : absDistPct < 2 ? "#dc3545"
    : absDistPct < 4 ? "#fd7e14"
    : "#28a745";

  if (options.length === 0) {
    return (
      <div style={emptyStyle}>
        No straddle positions yet.<br />Suggestions will appear when rules fire.
      </div>
    );
  }

  return (
    <div>
      {/* Strike + distance */}
      <div style={{ textAlign: "center", padding: "8px 0 12px", borderBottom: "1px solid #e9ecef", marginBottom: 10 }}>
        <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: "0.4px" }}>Straddle Strike</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: "#2c3e50", margin: "2px 0" }}>
          {straddleStrike ? `$${straddleStrike}` : "—"}
        </div>
        {distFromStrike != null && (
          <div style={{ fontSize: 12, fontWeight: 600, color: riskColor }}>
            {ltp > straddleStrike ? "+" : ""}{fmtPct(distFromStrike)} from strike
            {absDistPct < 2 && <span style={{ marginLeft: 6, fontSize: 10 }}>⚠ Near ATM</span>}
          </div>
        )}
      </div>

      {/* Key metrics */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
        <StatChip label="Net Δ"     value={fmt2(netDelta)} color={Math.abs(netDelta) < 0.15 ? "#28a745" : "#fd7e14"} />
        <StatChip label="Theta/day" value={fmtDollar(netTheta)} color="#28a745" />
        <StatChip label="Premium"   value={fmtDollar(totalPremium)} />
      </div>

      {/* Legs */}
      {calls.length > 0 && (
        <>
          <div style={legHeader}>Short Call</div>
          {calls.map((o, i) => (
            <div key={i}>
              <InfoRow label="Strike" value={`$${o.strike}`} />
              <InfoRow label="LTP"    value={fmtDollar(o.ltp)} />
              <InfoRow label="Qty"    value={`${o.netqty} lots`} />
            </div>
          ))}
        </>
      )}
      {puts.length > 0 && (
        <>
          <div style={{ ...legHeader, marginTop: 6 }}>Short Put</div>
          {puts.map((o, i) => (
            <div key={i}>
              <InfoRow label="Strike" value={`$${o.strike}`} />
              <InfoRow label="LTP"    value={fmtDollar(o.ltp)} />
              <InfoRow label="Qty"    value={`${o.netqty} lots`} />
            </div>
          ))}
        </>
      )}

      <div style={{ marginTop: 8, padding: "6px 0", borderTop: "1px solid #e9ecef" }}>
        <InfoRow
          label="Net Options P&L"
          value={fmtDollar(totalUnrealised)}
          color={totalUnrealised >= 0 ? "#28a745" : "#dc3545"}
        />
      </div>
    </div>
  );
}

// ── Exported default ──────────────────────────────────────────────────────────

export default function StrategyInfoPanel({ frame, strategyType }) {
  const title = strategyType === "DELTA_HEDGED_SHORT_STRANGLE"
    ? "Strangle"
    : strategyType === "WEEKLY_INDEX_SHORT_STRADDLE"
    ? "Straddle"
    : null;

  if (!title) return null;  // PH/FPH: caller handles LadderView

  return (
    <div>
      <div style={panelTitle}>{title}</div>
      {strategyType === "DELTA_HEDGED_SHORT_STRANGLE"
        ? <DhssPanel frame={frame} />
        : <WissPanel frame={frame} />
      }
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const panelTitle = {
  fontSize: 11, fontWeight: 700, color: "#6c757d",
  textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 10,
};
const legHeader = {
  fontSize: 10, fontWeight: 700, color: "#3b82f6",
  textTransform: "uppercase", letterSpacing: "0.4px",
  margin: "6px 0 4px",
};
const emptyStyle = {
  fontSize: 11, color: "#bbb", textAlign: "center",
  padding: "16px 4px", lineHeight: 1.5,
};
